// BUILD-046 correction cycle 1bb88b3e (stage B2a, item N9): the ONE "More actions" menu of a task row.
//
// The less-used row actions (Edit, Hand off, Reassign…, Return to queue, Assign to…, Recovery assignment…, Cancel request…) were a row of small buttons and
// unexplained icons. They now live in one menu per row:
//   - the trigger is a real <button> with a TEXT label ("More actions"), aria-haspopup="menu", aria-expanded, an accessible name that names the task and a
//     tooltip, and a visible focus ring;
//   - Enter / Space / ArrowDown opens it and focuses the first item; ArrowUp / ArrowDown / Home / End move between items; Tab also moves through the items
//     (every item is a tab stop) and the menu closes when focus leaves it; Escape closes it and returns focus to the trigger; a click outside closes it;
//   - choosing an item closes the menu and runs its action (the action opens its own picker or confirmation, nothing is sent by choosing).
// Nothing here knows what an entry does. It renders nothing when there are no entries.
//
// Owner report 2026-10-22: the menu was cut off where it dropped past the edge of the task box (the task table is overflow-hidden). It is now drawn on the page body, placed against its button with
// fixed coordinates (menuPlacement.ts: below the button, above it when there is no room below, inside the window sideways, scrolling inside itself when neither side has room), and follows the button
// when the page scrolls or the window is resized. Keys, focus and the accessible names are unchanged.
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { CSSProperties, KeyboardEvent } from 'react';
import { placeMenu } from './menuPlacement';
import type { MenuPosition } from './menuPlacement';

export interface MenuEntry {
  key: string;
  label: string;
  /** Accessible name when the label alone is not enough (for example it should name the task). */
  ariaLabel?: string;
  onSelect: () => void;
  disabled?: boolean;
  /**
   * Owner direction 2026-10-23: a disabled entry that carries a reason. It is drawn with the reason under its label, stays reachable by keyboard (aria-disabled, not removed from the tab order) so the reason
   * is read, and does nothing when chosen. Without a hint a disabled entry is an ordinary disabled button.
   */
  hint?: string;
  /** Draws the entry as destructive (cancel). */
  danger?: boolean;
}

export interface RowMoreMenuProps {
  entries: readonly MenuEntry[];
  /** The task's title, used in the accessible name of the trigger. */
  taskTitle: string;
  /** Visible text of the trigger. */
  label?: string;
}

export function RowMoreMenu({ entries, taskTitle, label = 'More actions' }: RowMoreMenuProps) {
  const [open, setOpen] = useState(false);
  const menuId = useId();
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const [position, setPosition] = useState<MenuPosition | null>(null);

  // The button and the menu are two places in the page (the menu is drawn on the page body): "inside" means either.
  const isInside = (node: Node): boolean => (wrapRef.current !== null && wrapRef.current.contains(node)) || (menuRef.current !== null && menuRef.current.contains(node));

  // A click outside the menu closes it.
  useEffect(() => {
    if (!open) return undefined;
    function onPointerDown(event: MouseEvent): void {
      if (event.target instanceof Node && !isInside(event.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Placed against the button before the browser paints, so the menu never shows at the wrong place; placed again when the page scrolls or the window changes size.
  useLayoutEffect(() => {
    if (!open) {
      setPosition(null);
      return undefined;
    }
    function place(): void {
      const trigger = triggerRef.current;
      const menu = menuRef.current;
      if (trigger === null || menu === null) return;
      const rect = trigger.getBoundingClientRect();
      // The natural height of the entries (a max-height may be clipping them now), plus the border.
      const natural = menu.scrollHeight + (menu.offsetHeight - menu.clientHeight);
      const next = placeMenu(rect, { width: menu.offsetWidth, height: natural }, { width: window.innerWidth, height: window.innerHeight });
      setPosition((previous) => (previous !== null && previous.top === next.top && previous.left === next.left && previous.maxHeight === next.maxHeight && previous.above === next.above ? previous : next));
    }
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open, entries.length]);

  // Opening by keyboard puts focus on the first enabled item.
  const focusOnOpen = useRef<'first' | 'last' | null>(null);
  useEffect(() => {
    if (!open || focusOnOpen.current === null) return;
    const items = itemRefs.current.filter((el): el is HTMLButtonElement => el !== null && !el.disabled);
    const target = focusOnOpen.current === 'last' ? items[items.length - 1] : items[0];
    focusOnOpen.current = null;
    target?.focus();
  }, [open]);

  if (entries.length === 0) return null;

  // Until it is measured the menu is in the layout but invisible (so its items can already take focus); after that it sits at fixed coordinates against the button.
  const menuStyle: CSSProperties = position === null ? { position: 'fixed', top: 0, left: 0, opacity: 0, pointerEvents: 'none' } : { position: 'fixed', top: position.top, left: position.left, maxHeight: position.maxHeight };

  function close(returnFocus: boolean): void {
    setOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  }

  function moveFocus(delta: 1 | -1 | 'first' | 'last'): void {
    const items = itemRefs.current.filter((el): el is HTMLButtonElement => el !== null && !el.disabled);
    if (items.length === 0) return;
    const at = items.findIndex((el) => el === document.activeElement);
    let next = 0;
    if (delta === 'first') next = 0;
    else if (delta === 'last') next = items.length - 1;
    else next = at < 0 ? (delta === 1 ? 0 : items.length - 1) : (at + delta + items.length) % items.length;
    items[next]?.focus();
  }

  function onTriggerKeyDown(event: KeyboardEvent<HTMLButtonElement>): void {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (open) {
        // Already open (the trigger was activated first): the arrow moves into the items.
        moveFocus(event.key === 'ArrowUp' ? 'last' : 'first');
        return;
      }
      focusOnOpen.current = event.key === 'ArrowUp' ? 'last' : 'first';
      setOpen(true);
    } else if (event.key === 'Escape' && open) {
      event.preventDefault();
      close(true);
    }
  }

  function onMenuKeyDown(event: KeyboardEvent<HTMLDivElement>): void {
    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        close(true);
        break;
      case 'ArrowDown':
        event.preventDefault();
        moveFocus(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        moveFocus(-1);
        break;
      case 'Home':
        event.preventDefault();
        moveFocus('first');
        break;
      case 'End':
        event.preventDefault();
        moveFocus('last');
        break;
      default:
        break;
    }
  }

  return (
    <div
      ref={wrapRef}
      className="relative inline-block"
      onBlur={(event) => {
        // Focus moved (Tab) to something outside the whole menu: close without stealing focus back. A click elsewhere is the mousedown handler's.
        const next = event.relatedTarget;
        if (open && next instanceof Node && !isInside(next)) setOpen(false);
      }}
    >
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={`${label}: ${taskTitle}`}
        title={label}
        onClick={(event) => {
          // A click made by Enter or Space (`detail` is 0) opens the menu and focuses its first item, as the menu-button pattern asks; a pointer click leaves focus.
          focusOnOpen.current = !open && event.detail === 0 ? 'first' : null;
          setOpen((value) => !value);
        }}
        onKeyDown={onTriggerKeyDown}
        className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2a7fb8]"
      >
        {/* Layout-4: an icon button (the three dots); its name is "More actions: <task>" and its tooltip "More actions". */}
        <svg aria-hidden="true" viewBox="0 0 20 20" className="h-4 w-4" fill="currentColor">
          <circle cx="4.5" cy="10" r="1.6" />
          <circle cx="10" cy="10" r="1.6" />
          <circle cx="15.5" cy="10" r="1.6" />
        </svg>
      </button>
      {open &&
        createPortal(
          <div
            ref={menuRef}
            id={menuId}
            role="menu"
            aria-label={`Actions for ${taskTitle}`}
            data-testid="row-more-menu"
            data-placement={position === null ? undefined : position.above ? 'above' : 'below'}
            onKeyDown={onMenuKeyDown}
            style={menuStyle}
            className="z-50 min-w-[12rem] max-w-[20rem] overflow-y-auto rounded border border-gray-200 bg-white py-1 shadow-lg"
          >
            {entries.map((entry, index) => {
              // An entry that is disabled WITH a reason stays focusable and shows the reason; choosing it does nothing and keeps the menu open.
              const explained = entry.disabled === true && entry.hint !== undefined;
              return (
                <button
                  key={entry.key}
                  ref={(el) => {
                    itemRefs.current[index] = el;
                  }}
                  type="button"
                  role="menuitem"
                  disabled={entry.disabled === true && !explained}
                  aria-disabled={explained ? true : undefined}
                  aria-label={entry.ariaLabel}
                  data-testid={explained ? 'menu-entry-explained' : undefined}
                  onClick={() => {
                    if (explained) return;
                    setOpen(false);
                    // Focus goes back to the "More actions" button BEFORE the action runs, so a dialog the action opens (Hand off) remembers a control that still
                    // exists and gives focus back to it when it closes (the menu item itself is gone by then).
                    triggerRef.current?.focus();
                    entry.onSelect();
                  }}
                  className={`block w-full px-3 py-2 text-left text-sm focus:outline-none focus-visible:bg-sky-50 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#4AABDE] disabled:opacity-50 ${
                    explained ? 'cursor-not-allowed text-gray-500' : entry.danger === true ? 'text-red-700 hover:bg-red-50' : 'text-gray-800 hover:bg-gray-50'
                  }`}
                >
                  {entry.label}
                  {explained && (
                    <span className="mt-0.5 block text-xs font-normal text-gray-600" data-testid="menu-entry-hint">
                      {entry.hint}
                    </span>
                  )}
                </button>
              );
            })}
          </div>,
          document.body
        )}
    </div>
  );
}
